import { AppError } from '../../api/errors.js';
import type {
  DatasetGroupRoles,
  IAccessControlService,
} from '../../services/accessControlService.interface.js';
import type { Workspace } from '../../types/workspace.js';

import type {
  CreatePrivateWorkspaceRequest,
  CreatePrivateWorkspaceResult,
  ConvertPrivateWorkspaceRequest,
  DeletePrivateWorkspaceDependencies,
  PrivateWorkspaceCreationPlan,
  PrivateWorkspaceDependencies,
  PrivateWorkspaceRoles,
} from './privateWorkspace.interface.js';

export const PRIVATE_WORKSPACE_DATASET_SUBTYPE = 'workspace-data';
export const PRIVATE_WORKSPACE_DATASET_DATA_TYPE = 'Generic';
export const PRIVATE_WORKSPACE_SENSITIVITY_LABEL = 'confidential';
export const PRIVATE_WORKSPACE_DATASET_SUFFIX = '-workspace';
const UNKNOWN_WORKSPACE_CREATOR = 'unknown';
const WORKSPACE_CONVERSION_OWNER_ERROR =
  'Only the original workspace creator can convert a public workspace to private.';

export function isWorkspaceCreator(
  workspace: Workspace,
  callerIdentifiers: readonly (string | undefined)[],
): boolean {
  const creator = workspace.systemProperties.createdBy?.toLowerCase();
  return (
    !!creator?.trim() &&
    creator !== UNKNOWN_WORKSPACE_CREATOR &&
    callerIdentifiers.some(
      (identifier) => identifier?.toLowerCase() === creator,
    )
  );
}

export class PrivateWorkspaceCompensationError extends Error {
  readonly code = 'PrivateWorkspaceCompensationFailed';

  constructor(
    message: string,
    readonly operationError: unknown,
    readonly compensationError: unknown,
  ) {
    super(message);
  }
}

export class PrivateWorkspaceCleanupError extends Error {
  readonly code = 'PrivateWorkspaceCleanupFailed';

  constructor(
    readonly workspaceName: string,
    readonly anchorDatasetId: string,
    readonly cause: unknown,
  ) {
    super(
      `Workspace '${workspaceName}' was deleted, but its access dataset could not be cleaned up.`,
    );
  }
}

export function mapWorkspaceRolesToDatasetRoles(roles: PrivateWorkspaceRoles) {
  return {
    owner: roles.owner,
    contributor: roles.contributor,
    consumer: roles.reader,
    annotator: [],
    restrictedConsumer: [],
  };
}

export function mapDatasetRolesToWorkspaceRoles(
  roles: DatasetGroupRoles,
): PrivateWorkspaceRoles {
  return {
    owner: roles.owner,
    contributor: roles.contributor,
    reader: roles.consumer,
  };
}

export function hasWorkspaceRoleAssignments(
  roles: PrivateWorkspaceRoles,
): boolean {
  return (
    roles.owner.length > 0 ||
    roles.contributor.length > 0 ||
    roles.reader.length > 0
  );
}

function validateExplicitRoleAssignments(roles: PrivateWorkspaceRoles): void {
  if (hasWorkspaceRoleAssignments(roles) && roles.owner.length === 0) {
    throw new Error(
      'Select at least one Owner group before configuring workspace access.',
    );
  }
}

function mergeRoleChanges(
  latest: string[],
  original: string[],
  edited: string[],
): string[] {
  const originalIds = new Set(original);
  const editedIds = new Set(edited);
  const removedIds = new Set(
    original.filter((groupId) => !editedIds.has(groupId)),
  );
  const merged = latest.filter((groupId) => !removedIds.has(groupId));
  const mergedIds = new Set(merged);

  for (const groupId of edited) {
    if (!originalIds.has(groupId) && !mergedIds.has(groupId)) {
      merged.push(groupId);
      mergedIds.add(groupId);
    }
  }

  return merged;
}

function mergeWorkspaceRoleChanges(
  latest: DatasetGroupRoles,
  original: PrivateWorkspaceRoles,
  edited: PrivateWorkspaceRoles,
): DatasetGroupRoles {
  return {
    owner: mergeRoleChanges(latest.owner, original.owner, edited.owner),
    contributor: mergeRoleChanges(
      latest.contributor,
      original.contributor,
      edited.contributor,
    ),
    consumer: mergeRoleChanges(latest.consumer, original.reader, edited.reader),
    annotator: latest.annotator,
    restrictedConsumer: latest.restrictedConsumer,
  };
}

export function buildPrivateWorkspacePlan(
  request: CreatePrivateWorkspaceRequest,
  anchorDatasetId: string,
): PrivateWorkspaceCreationPlan {
  return {
    workspaceBody: {
      displayName: request.displayName,
      description: request.description,
      accessControl: {
        aclAnchorDatasetId: anchorDatasetId,
        publisherAppIds: [],
      },
    },
    datasetRoles: mapWorkspaceRolesToDatasetRoles(request.roles),
  };
}

async function createAnchorDataset(
  workspaceName: string,
  dependencies: PrivateWorkspaceDependencies,
) {
  return dependencies.dataHubService.createDataset({
    name: `${workspaceName}${PRIVATE_WORKSPACE_DATASET_SUFFIX}`,
    description: `Access control for workspace ${workspaceName}`,
    accessCategory: 'Restricted',
    dataType: PRIVATE_WORKSPACE_DATASET_DATA_TYPE,
    sensitivityLabel: PRIVATE_WORKSPACE_SENSITIVITY_LABEL,
    subType: PRIVATE_WORKSPACE_DATASET_SUBTYPE,
  });
}

export async function createPrivateWorkspace(
  request: CreatePrivateWorkspaceRequest,
  dependencies: PrivateWorkspaceDependencies,
): Promise<CreatePrivateWorkspaceResult> {
  const anchorDataset = await createAnchorDataset(request.name, dependencies);

  try {
    const plan = buildPrivateWorkspacePlan(request, anchorDataset.id);
    validateExplicitRoleAssignments(request.roles);
    const publishedAnchorDataset =
      await dependencies.dataHubService.publishDataset(anchorDataset.id);
    if (hasWorkspaceRoleAssignments(request.roles)) {
      await dependencies.accessControlService.createDatasetGroupRole({
        dataSetId: anchorDataset.id,
        roles: plan.datasetRoles,
      });
    }
    const workspace = await dependencies.workspaceService.create(
      request.name,
      plan.workspaceBody,
    );
    return { workspace, anchorDataset: publishedAnchorDataset };
  } catch (operationError) {
    try {
      await dependencies.dataHubService.deleteDataset(anchorDataset.id);
    } catch (compensationError) {
      throw new PrivateWorkspaceCompensationError(
        'Private workspace creation failed and its access dataset could not be cleaned up.',
        operationError,
        compensationError,
      );
    }

    throw operationError;
  }
}

export async function convertWorkspaceToPrivate(
  request: ConvertPrivateWorkspaceRequest,
  dependencies: PrivateWorkspaceDependencies,
): Promise<CreatePrivateWorkspaceResult> {
  if (request.workspace.accessControl != null) {
    throw new Error(
      `Workspace '${request.workspace.name}' is already private.`,
    );
  }
  if (request.workspace.etag == null) {
    throw new Error(
      `Workspace '${request.workspace.name}' cannot be converted without an ETag.`,
    );
  }
  if (!isWorkspaceCreator(request.workspace, request.callerIdentifiers)) {
    throw new AppError(WORKSPACE_CONVERSION_OWNER_ERROR, 'HTTP_403', 403);
  }

  const anchorDataset = await createAnchorDataset(
    request.workspace.name,
    dependencies,
  );
  try {
    validateExplicitRoleAssignments(request.roles);
    const publishedAnchorDataset =
      await dependencies.dataHubService.publishDataset(anchorDataset.id);
    if (hasWorkspaceRoleAssignments(request.roles)) {
      await dependencies.accessControlService.createDatasetGroupRole({
        dataSetId: anchorDataset.id,
        roles: mapWorkspaceRolesToDatasetRoles(request.roles),
      });
    }
    const workspace = await dependencies.workspaceService.patch(
      request.workspace.name,
      {
        accessControl: {
          aclAnchorDatasetId: anchorDataset.id,
          publisherAppIds: [],
        },
      },
      request.workspace.etag,
    );
    return { workspace, anchorDataset: publishedAnchorDataset };
  } catch (operationError) {
    try {
      await dependencies.dataHubService.deleteDataset(anchorDataset.id);
    } catch (compensationError) {
      throw new PrivateWorkspaceCompensationError(
        'Workspace conversion failed and its access dataset could not be cleaned up.',
        operationError,
        compensationError,
      );
    }
    throw operationError;
  }
}

export async function updatePrivateWorkspaceRoles(
  anchorDatasetId: string,
  originalRoles: PrivateWorkspaceRoles,
  roles: PrivateWorkspaceRoles,
  accessControlService: Pick<
    IAccessControlService,
    'createDatasetGroupRole' | 'getDatasetGroupRole' | 'updateDatasetGroupRole'
  >,
): Promise<void> {
  validateExplicitRoleAssignments(roles);

  let latest: Awaited<ReturnType<IAccessControlService['getDatasetGroupRole']>>;
  try {
    latest = await accessControlService.getDatasetGroupRole(anchorDatasetId);
  } catch (error) {
    if (!(error instanceof AppError) || error.statusCode !== 404) {
      throw error;
    }

    await accessControlService.createDatasetGroupRole({
      dataSetId: anchorDatasetId,
      roles: mapWorkspaceRolesToDatasetRoles(roles),
    });
    return;
  }

  await accessControlService.updateDatasetGroupRole(anchorDatasetId, {
    roles: mergeWorkspaceRoleChanges(latest.roles, originalRoles, roles),
  });
}

export async function deletePrivateWorkspace(
  workspaceName: string,
  anchorDatasetId: string | null,
  dependencies: DeletePrivateWorkspaceDependencies,
): Promise<void> {
  await dependencies.workspaceService.delete(workspaceName);
  if (anchorDatasetId === null) return;

  try {
    await dependencies.dataHubService.deleteDataset(anchorDatasetId);
  } catch (cause) {
    throw new PrivateWorkspaceCleanupError(
      workspaceName,
      anchorDatasetId,
      cause,
    );
  }
}
